import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cities, regions } from '../data/citiesData';
import { formatILS, formatPercent } from '../utils/format';
import RiskBadge from '../components/ui/RiskBadge';
import ScoreBar from '../components/ui/ScoreBar';
import imgBuilding from '../assets/images/building-sea-view.jpg';

export default function Cities() {
  const [regionFilter, setRegionFilter] = useState('all');
  const [sortKey, setSortKey] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);

  const enriched = cities.map(c => {
    const avg = c.rentalYields.reduce((a, b) => a + b.grossYield, 0) / c.rentalYields.length;
    return { ...c, avgYield: avg };
  });

  const filtered = enriched.filter(c => regionFilter === 'all' || c.region === regionFilter);
  const sorted = [...filtered].sort((a, b) => {
    const va = a[sortKey], vb = b[sortKey];
    if (typeof va === 'string') return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortAsc ? va - vb : vb - va;
  });

  const handleSort = key => { if (sortKey === key) setSortAsc(!sortAsc); else { setSortKey(key); setSortAsc(true); } };
  const sortIcon = key => sortKey !== key ? ' ↕' : sortAsc ? ' ↑' : ' ↓';

  const filterBtnStyle = (active) => ({
    padding: '5px 16px',
    borderRadius: 'var(--radius-sm)',
    border: `1.5px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
    cursor: 'pointer',
    fontSize: '0.78rem',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    transition: 'all 0.2s',
    background: active ? 'var(--color-primary)' : 'var(--color-bg-white)',
    color: active ? '#fafaf8' : 'var(--color-text-muted)',
  });

  return (
    <div className="page-content">
      {/* Banner photo */}
      <div style={{
        height: 260,
        backgroundImage: `linear-gradient(rgba(13,31,60,0.68), rgba(13,31,60,0.78)), url(${imgBuilding})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 40%',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '3rem',
      }}>
        <div className="container">
          <span className="gold-tag">Comparatif</span>
          <h1 style={{ color: '#fafaf8', marginTop: '0.5rem', marginBottom: '0.5rem' }}>Villes d'Israël</h1>
          <p style={{ color: 'rgba(250,250,248,0.75)', margin: 0, fontSize: '1rem' }}>
            Comparez les prix au m², rendements et profils de risque des <strong style={{ color: '#fafaf8' }}>{cities.length} principales villes</strong>.
          </p>
        </div>
      </div>
      <div className="container">
        <p style={{ marginBottom: '2.5rem', fontSize: '1.05rem', display: 'none' }}>
          Comparez les prix au m², rendements et profils de risque des <strong>{cities.length} principales villes</strong>.
        </p>

        {/* Filtres */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', minWidth: 70 }}>Région</span>
            {['all', ...regions].map(r => (
              <button key={r} onClick={() => setRegionFilter(r)} style={filterBtnStyle(regionFilter === r)}>
                {r === 'all' ? 'Toutes' : r}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', minWidth: 70 }}>Trier</span>
            {[['name', 'Ville'], ['averagePricePerSqm', 'Prix/m²'], ['avgYield', 'Rendement'], ['attractivenessScore', 'Attractivité']].map(([k, l]) => (
              <button key={k} onClick={() => handleSort(k)} style={filterBtnStyle(sortKey === k)}>
                {l}{sortIcon(k)}
              </button>
            ))}
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')}>Ville{sortIcon('name')}</th>
                <th>Région</th>
                <th onClick={() => handleSort('averagePricePerSqm')}>Prix moy./m²{sortIcon('averagePricePerSqm')}</th>
                <th>Fourchette</th>
                <th onClick={() => handleSort('avgYield')}>Rendement brut{sortIcon('avgYield')}</th>
                <th onClick={() => handleSort('attractivenessScore')}>Attractivité{sortIcon('attractivenessScore')}</th>
                <th>Risque</th>
                <th>Fiche</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(city => (
                <tr key={city.id}>
                  <td><strong style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-display)' }}>{city.name}</strong></td>
                  <td><span className="badge badge-blue">{city.region}</span></td>
                  <td><strong>{formatILS(city.averagePricePerSqm)}</strong></td>
                  <td><span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{formatILS(city.priceRange.min, true)} – {formatILS(city.priceRange.max, true)}</span></td>
                  <td><span style={{ fontWeight: 700, color: city.avgYield >= 4 ? 'var(--color-success)' : 'var(--color-primary)' }}>{formatPercent(city.avgYield)}</span></td>
                  <td><ScoreBar score={city.attractivenessScore} /></td>
                  <td><RiskBadge level={city.riskLevel} /></td>
                  <td><Link to={`/villes/${city.slug}`} className="btn btn-primary btn-sm">Voir</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="insight-box" style={{ marginTop: '1.5rem' }}>
          Données indicatives 2024–2025. Rendements bruts avant charges et fiscalité.
        </div>

        {/* Opportunités du moment */}
        <section style={{ marginTop: '3rem' }}>
          <h2>Opportunités du moment</h2>
          <p style={{ marginBottom: '2rem' }}>Observations actuelles par marché — mise à jour régulière.</p>
          <div className="grid-3">
            {cities.map(city => (
              <div key={city.id} style={{ background: 'var(--color-bg-white)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', border: '1px solid var(--color-border)', borderLeft: '3px solid var(--color-accent)', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <strong style={{ fontFamily: 'var(--font-display)', color: 'var(--color-primary)', fontSize: '1.05rem' }}>{city.name}</strong>
                  <span className="badge badge-blue">{city.region}</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--color-text)' }}>{city.opportunityNote}</p>
                <Link to={`/villes/${city.slug}`} style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Voir la fiche</Link>
              </div>
            ))}
          </div>
          <div className="insight-box" style={{ marginTop: '1.5rem' }}>
            Ces observations sont basées sur l'analyse du marché israélien fin 2024 – début 2025. Elles ne constituent pas des recommandations d'investissement.
          </div>
        </section>
      </div>
    </div>
  );
}
